import React from 'react';
import { useListContext } from 'react-admin';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './dropdown-menu';
import { cn } from '../../lib/utils';

/**
 * Sort menu — DESIGN_SYSTEM.md §6/§8.
 * DropdownMenu of sortable fields wired to RA sort/setSort. Single-column;
 * picking the active field flips direction. The active sort also shows as a
 * neutral token via <FilterTokens/>.
 */
export type SortField = { field: string; label: string };

export function SortMenu({ fields }: { fields: SortField[] }) {
  const { sort, setSort } = useListContext();

  const pick = (field: string) => {
    const nextOrder = sort?.field === field && sort?.order === 'ASC' ? 'DESC' : 'ASC';
    setSort({ field, order: nextOrder });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-pill border border-hairline px-3 py-1.5 text-[13px] font-semibold text-ink-2 transition-colors duration-150 hover:text-ink"
        >
          <ArrowUpDown className="size-3.5" />
          Sort
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        <DropdownMenuLabel className="text-[11.5px] uppercase tracking-[0.06em] text-ink-2">
          Sort by
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {fields.map((f) => {
          const active = sort?.field === f.field;
          return (
            <DropdownMenuItem
              key={f.field}
              onClick={() => pick(f.field)}
              className={cn('flex items-center justify-between gap-3', active && 'text-indigo')}
            >
              {f.label}
              {active &&
                (sort?.order === 'ASC' ? (
                  <ArrowUp className="size-3.5" />
                ) : (
                  <ArrowDown className="size-3.5" />
                ))}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
